/**
 * SLIDE ROLES: what a slide is FOR, and which slots it needs filled.
 *
 * The third axis, and the one that was missing. A brand kit says what a document may look like; the nine
 * composition families say how it may be ARRANGED; neither says what belongs on it. So the writer invented
 * the answer every time, which is where the template tics came from — an eyebrow and three cards is what
 * you reach for when nobody told you what the slide is doing.
 *
 * HARVESTED, not authored. 61 pages of two real reference decks (a blank template library and a finished
 * branded sales deck) were read page by page, each described as a reusable role with its slots and the word
 * count actually observed in that slot, then collapsed into these 21. That provenance is the point: the word
 * caps below are measurements of what fits on a 1920x1080 slide, not preferences, which is why they are safe
 * to enforce as hard limits on generated copy.
 *
 * The `eyebrow` and `kicker` slots the harvest found are deliberately NOT here. The contract bans that device
 * by default; carrying a slot for it would be an invitation to use it.
 *
 * A TEMPLATE is a role plus a composition, rendered in a kit. Nothing here knows about a brand.
 */
import type { LayoutPattern } from './styleRecord'

/** What a slot holds. Drives nothing on its own; it is what the writer is told the slot is for. */
export type SlotKind =
  | 'headline'
  | 'subhead'
  | 'body'
  | 'label'
  | 'number'
  | 'unit'
  | 'caption'
  | 'quote'
  | 'attribution'
  | 'image'
  | 'logo'
  | 'chart'
  | 'item-title'
  | 'item-body'
  | 'index'

export interface RoleSlot {
  name: string
  kind: SlotKind
  /** Measured off the reference decks. Zero for a slot that holds no words (an image). */
  maxWords: number
  required: boolean
}

export interface SlideRole {
  id: string
  label: string
  /** What the slide is for, one sentence. */
  purpose: string
  /** The narrative moment it belongs to, and how to use it well. */
  whenToUse: string
  /** The geometry families that suit this role, best first. */
  compositions: LayoutPattern[]
  slots: RoleSlot[]
  /** The slot group that repeats, when the role repeats a unit. */
  repeatSlot?: string
  /** How many repetitions read well, e.g. "3-4". */
  repeatRange?: string
}

export const SLIDE_ROLES: SlideRole[] = [
  {
    id: 'cover',
    label: 'Cover',
    purpose:
      'Opens the deck: names the whole thing and states its promise in one line before any content starts.',
    whenToUse:
      'The first frame, and any re-open after an intermission. One title, at most one promise line, optionally a first look at the product and a footer wordmark.',
    compositions: ['stack'],
    slots: [
      {
        name: 'title',
        kind: 'headline',
        maxWords: 6,
        required: true,
      },
      {
        name: 'tagline',
        kind: 'subhead',
        maxWords: 10,
        required: false,
      },
      {
        name: 'image',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
      {
        name: 'brandmark',
        kind: 'logo',
        maxWords: 2,
        required: false,
      },
      {
        name: 'deckLabel',
        kind: 'caption',
        maxWords: 5,
        required: false,
      },
    ],
  },
  {
    id: 'section-divider',
    label: 'Section divider',
    purpose: 'Breaks the deck into chapters so the audience can reset before the next run of artifacts.',
    whenToUse:
      'Between chapters. Chapter name alone when the turn is obvious, plus one orientation sentence when it is not. Pick one divider shape and keep it for every divider in the deck.',
    compositions: ['stack'],
    slots: [
      {
        name: 'title',
        kind: 'headline',
        maxWords: 5,
        required: true,
      },
      {
        name: 'description',
        kind: 'subhead',
        maxWords: 8,
        required: false,
      },
    ],
  },
  {
    id: 'statement',
    label: 'Statement',
    purpose:
      'Gives one idea the whole artifact: the problem, the vision, or the single thing being claimed, with nothing to compete with it.',
    whenToUse:
      'Framing beats. Name the problem before showing product, or declare what the company does. The headline is a complete declarative sentence; any body only points at the fuller doc rather than explaining it.',
    compositions: ['stack', 'split'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 12,
        required: true,
      },
      {
        name: 'body',
        kind: 'body',
        maxWords: 30,
        required: false,
      },
      {
        name: 'image',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
    ],
  },
  {
    id: 'feature-list',
    label: 'Feature list',
    purpose:
      'Lays out a small set of parallel things under one framing headline, each with a short name and a line of context.',
    whenToUse:
      'The workhorse evidence artifact: capabilities, pillars, offerings. Items must be parallel in kind and grammar, and get equal billing with no ranking implied. Past four, add a row or duplicate the artifact rather than shrinking the type.',
    compositions: ['grid', 'split', 'ledger'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 4,
        required: true,
      },
      {
        name: 'subhead',
        kind: 'subhead',
        maxWords: 8,
        required: false,
      },
      {
        name: 'supportingImage',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
      {
        name: 'item.index',
        kind: 'index',
        maxWords: 1,
        required: false,
      },
      {
        name: 'item.image',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
      {
        name: 'item.title',
        kind: 'item-title',
        maxWords: 3,
        required: true,
      },
      {
        name: 'item.body',
        kind: 'item-body',
        maxWords: 10,
        required: true,
      },
    ],
    repeatSlot: 'item',
    repeatRange: '3-4',
  },
  {
    id: 'statement-cards',
    label: 'Statement cards',
    purpose:
      'Declares a small set of beliefs, rules, or audiences, each given its own panel so it lands as a claim rather than a bullet.',
    whenToUse:
      'When the items are assertions or segments, not features: principles, operating rules, who this is for. Each card carries one short claim; the empty space inside the card is what makes it read as a statement.',
    compositions: ['grid'],
    slots: [
      {
        name: 'sectionOwner',
        kind: 'label',
        maxWords: 3,
        required: false,
      },
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 4,
        required: true,
      },
      {
        name: 'backgroundImage',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
      {
        name: 'card.index',
        kind: 'index',
        maxWords: 2,
        required: false,
      },
      {
        name: 'card.icon',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
      {
        name: 'card.title',
        kind: 'item-title',
        maxWords: 3,
        required: true,
      },
      {
        name: 'card.body',
        kind: 'item-body',
        maxWords: 8,
        required: false,
      },
    ],
    repeatSlot: 'card',
    repeatRange: '3-4',
  },
  {
    id: 'process-steps',
    label: 'Sequence and steps',
    purpose:
      'Shows ordered stages read in one direction: dates along a line, or stages that narrow from top to bottom.',
    whenToUse:
      'Roadmaps, milestones, funnels, qualification pipelines. The geometry states the order or the narrowing, so the labels only have to name each step. Delete steps you do not need; three to six all balance.',
    compositions: ['spectrum'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 4,
        required: true,
      },
      {
        name: 'subhead',
        kind: 'subhead',
        maxWords: 14,
        required: false,
      },
      {
        name: 'step.label',
        kind: 'item-title',
        maxWords: 4,
        required: true,
      },
      {
        name: 'step.body',
        kind: 'item-body',
        maxWords: 25,
        required: false,
      },
    ],
    repeatSlot: 'step',
    repeatRange: '3-6',
  },
  {
    id: 'quadrant-matrix',
    label: 'Positioning matrix',
    purpose:
      'Positions a few options, players, or ideas against two independent variables so the trade-off space is visible instead of listed.',
    whenToUse:
      'Competitive landscape, prioritisation, where we sit. Axis end labels do the arguing; point labels stay to a couple of words. Drop the headline when the setup was already made on the previous artifact and the map itself is the payoff.',
    compositions: ['field'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 6,
        required: false,
      },
      {
        name: 'subhead',
        kind: 'subhead',
        maxWords: 14,
        required: false,
      },
      {
        name: 'axisXLeft',
        kind: 'label',
        maxWords: 3,
        required: true,
      },
      {
        name: 'axisXRight',
        kind: 'label',
        maxWords: 3,
        required: true,
      },
      {
        name: 'axisYTop',
        kind: 'label',
        maxWords: 3,
        required: true,
      },
      {
        name: 'axisYBottom',
        kind: 'label',
        maxWords: 3,
        required: true,
      },
      {
        name: 'point.label',
        kind: 'item-title',
        maxWords: 3,
        required: true,
      },
    ],
    repeatSlot: 'point',
    repeatRange: '3-5',
  },
  {
    id: 'concept-relationship',
    label: 'Concept relationship',
    purpose:
      'Shows how a handful of concepts sit against each other: what overlaps, what is contained inside what, what narrows to the core.',
    whenToUse:
      'Scope, focus, or shared ground. Zones are not interchangeable list items, so each label must name a distinct region: outer to inner for containment, left / shared / right for overlap.',
    compositions: ['orbit', 'split'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 4,
        required: true,
      },
      {
        name: 'body',
        kind: 'body',
        maxWords: 14,
        required: false,
      },
      {
        name: 'zone.label',
        kind: 'label',
        maxWords: 3,
        required: true,
      },
    ],
    repeatSlot: 'zone',
    repeatRange: '2-4',
  },
  {
    id: 'metric-proof',
    label: 'Metrics',
    purpose:
      'Proves the argument with numbers: one figure that is the whole point, or a set of figures read together with a paragraph saying what they add up to.',
    whenToUse:
      'Traction, results, scale. Figures stay to three or four characters; interpretation lives either in the shared paragraph or in the per-metric caption, never both. One number for a single hero stat, up to four when they are read as a group.',
    compositions: ['grid', 'ledger', 'single-number', 'split'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 4,
        required: false,
      },
      {
        name: 'body',
        kind: 'body',
        maxWords: 18,
        required: false,
      },
      {
        name: 'metric.number',
        kind: 'number',
        maxWords: 1,
        required: true,
      },
      {
        name: 'metric.label',
        kind: 'item-title',
        maxWords: 3,
        required: false,
      },
      {
        name: 'metric.body',
        kind: 'item-body',
        maxWords: 8,
        required: false,
      },
      {
        name: 'caption',
        kind: 'caption',
        maxWords: 9,
        required: false,
      },
    ],
    repeatSlot: 'metric',
    repeatRange: '1-4',
  },
  {
    id: 'case-study',
    label: 'Case study',
    purpose:
      "Tells one named customer's story as continuous prose, anchored by a photograph of the real people.",
    whenToUse:
      'After the capability artifacts, when one worked example carries more weight than another list. Prose, no bullets, with one hard number landing in the last sentence.',
    compositions: ['stack', 'split'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 8,
        required: true,
      },
      {
        name: 'body',
        kind: 'body',
        maxWords: 70,
        required: true,
      },
      {
        name: 'bannerImage',
        kind: 'image',
        maxWords: 0,
        required: true,
      },
    ],
  },
  {
    id: 'quote',
    label: 'Quote',
    purpose: 'Lets one piece of testimony carry a whole page, with a face attached to the words.',
    whenToUse:
      'When one voice should be remembered rather than weighed against others. Keep it to two or three lines of type; past twenty words the block breaks.',
    compositions: ['stack', 'split'],
    slots: [
      {
        name: 'portrait',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
      {
        name: 'quote',
        kind: 'quote',
        maxWords: 20,
        required: true,
      },
      {
        name: 'name',
        kind: 'attribution',
        maxWords: 3,
        required: true,
      },
      {
        name: 'attributionDetail',
        kind: 'caption',
        maxWords: 6,
        required: false,
      },
      {
        name: 'logo',
        kind: 'logo',
        maxWords: 3,
        required: false,
      },
    ],
  },
  {
    id: 'quote-grid',
    label: 'Quote grid',
    purpose:
      'Sets several short pieces of testimony side by side so they are weighed together as consensus rather than one at a time.',
    whenToUse:
      'Social proof in bulk. Quotes must be near equal length or the row goes ragged; the more columns, the shorter each quote. Avatars are optional and either all present or all absent.',
    compositions: ['grid'],
    slots: [
      {
        name: 'testimonial.avatar',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
      {
        name: 'testimonial.quote',
        kind: 'quote',
        maxWords: 12,
        required: true,
      },
      {
        name: 'testimonial.name',
        kind: 'attribution',
        maxWords: 3,
        required: true,
      },
      {
        name: 'testimonial.location',
        kind: 'caption',
        maxWords: 3,
        required: false,
      },
    ],
    repeatSlot: 'testimonial',
    repeatRange: '2-4',
  },
  {
    id: 'image-showcase',
    label: 'Image showcase',
    purpose:
      'Puts one supporting picture, screen, or device next to a short written explanation of what the viewer is looking at.',
    whenToUse:
      'Product walkthroughs and any idea that needs one visual beside it. Text sets the picture up and stops; the image carries equal or greater weight. Two screens side by side when the point is a before and after or two device sizes.',
    compositions: ['split', 'grid'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 5,
        required: true,
      },
      {
        name: 'body',
        kind: 'body',
        maxWords: 25,
        required: true,
      },
      {
        name: 'image',
        kind: 'image',
        maxWords: 0,
        required: true,
      },
    ],
    repeatSlot: 'image',
    repeatRange: '1-2',
  },
  {
    id: 'image-gallery',
    label: 'Image gallery',
    purpose: 'Shows several pictures or screens together so they read as one set rather than one hero.',
    whenToUse:
      'A flow, a product tour, a mosaic of surfaces. Captions are optional and short; when the images can speak alone, drop all text and let the narration do the work. Past three or four, add a row or duplicate the artifact.',
    compositions: ['grid'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 5,
        required: false,
      },
      {
        name: 'item.image',
        kind: 'image',
        maxWords: 0,
        required: true,
      },
      {
        name: 'item.title',
        kind: 'item-title',
        maxWords: 4,
        required: false,
      },
      {
        name: 'item.body',
        kind: 'item-body',
        maxWords: 18,
        required: false,
      },
      {
        name: 'item.caption',
        kind: 'caption',
        maxWords: 6,
        required: false,
      },
    ],
    repeatSlot: 'item',
    repeatRange: '2-4',
  },
  {
    id: 'full-bleed-image',
    label: 'Full-bleed image',
    purpose: 'Gives one picture the entire frame with no deck copy, or at most a line naming it.',
    whenToUse:
      'The payoff right after a statement or a setup artifact. The image has to survive being read at full size, because there is nothing else on the page.',
    compositions: ['full-bleed'],
    slots: [
      {
        name: 'image',
        kind: 'image',
        maxWords: 0,
        required: true,
      },
      {
        name: 'caption',
        kind: 'caption',
        maxWords: 10,
        required: false,
      },
    ],
  },
  {
    id: 'annotated-visual',
    label: 'Annotated visual',
    purpose:
      'Calls out specific parts of one screen or object by name so the audience looks where the speaker is pointing.',
    whenToUse:
      'Detail beats inside a product section. Labels are one to three words on leader lines and carry all the language; there is no headline or body to lean on, so the anchor points are the argument.',
    compositions: ['orbit'],
    slots: [
      {
        name: 'image',
        kind: 'image',
        maxWords: 0,
        required: true,
      },
      {
        name: 'callout.label',
        kind: 'label',
        maxWords: 3,
        required: true,
      },
    ],
    repeatSlot: 'callout',
    repeatRange: '2-4',
  },
  {
    id: 'person-profile',
    label: 'Person profile',
    purpose: 'Puts a face and a short biography behind the company, usually the founder or a key hire.',
    whenToUse:
      'Late in the deck, before the ask. One person against one paragraph; the portrait carries the weight and the bio stays a single narrow column of prose.',
    compositions: ['split'],
    slots: [
      {
        name: 'personName',
        kind: 'headline',
        maxWords: 4,
        required: true,
      },
      {
        name: 'bio',
        kind: 'body',
        maxWords: 55,
        required: true,
      },
      {
        name: 'portrait',
        kind: 'image',
        maxWords: 0,
        required: true,
      },
      {
        name: 'supportingImage',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
    ],
  },
  {
    id: 'team-grid',
    label: 'Team',
    purpose:
      'Introduces everyone at once, faces or fields, where credibility comes from completeness rather than any one entry.',
    whenToUse:
      'The team artifact, or any set of records sharing the same fields. Names and roles are short labels, never sentences; the layout absorbs an odd count. Roughly six to twelve entries before the type has to shrink.',
    compositions: ['grid', 'ledger'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 4,
        required: true,
      },
      {
        name: 'columnLabel',
        kind: 'label',
        maxWords: 2,
        required: false,
      },
      {
        name: 'member.photo',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
      {
        name: 'member.name',
        kind: 'item-title',
        maxWords: 3,
        required: true,
      },
      {
        name: 'member.role',
        kind: 'item-body',
        maxWords: 3,
        required: true,
      },
      {
        name: 'member.detail',
        kind: 'item-body',
        maxWords: 3,
        required: false,
      },
      {
        name: 'member.contact',
        kind: 'caption',
        maxWords: 3,
        required: false,
      },
    ],
    repeatSlot: 'member',
    repeatRange: '6-12',
  },
  {
    id: 'live-poll',
    label: 'Live poll',
    purpose:
      'Asks the room to react or to choose between named options, and shows the split without stopping for discussion.',
    whenToUse:
      'An interaction beat mid-presentation. One prompt inside one card on an otherwise empty page; the surrounding whitespace is what makes the single interaction the only thing to do. Options repeat cleanly to about five.',
    compositions: ['stack'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 8,
        required: true,
      },
      {
        name: 'prompt',
        kind: 'item-title',
        maxWords: 8,
        required: true,
      },
      {
        name: 'option.label',
        kind: 'item-body',
        maxWords: 6,
        required: false,
      },
      {
        name: 'option.result',
        kind: 'number',
        maxWords: 1,
        required: false,
      },
      {
        name: 'emptyState',
        kind: 'caption',
        maxWords: 4,
        required: false,
      },
    ],
    repeatSlot: 'option',
    repeatRange: '2-5',
  },
  {
    id: 'closing',
    label: 'Closing ask',
    purpose: 'Ends on the ask: what happens next, stated as an action, with a last look at the product.',
    whenToUse:
      'The final content artifact. Copy is short and imperative because the buttons carry the ask; one primary action, at most one secondary.',
    compositions: ['split'],
    slots: [
      {
        name: 'headline',
        kind: 'headline',
        maxWords: 5,
        required: true,
      },
      {
        name: 'subhead',
        kind: 'subhead',
        maxWords: 16,
        required: true,
      },
      {
        name: 'primaryAction',
        kind: 'label',
        maxWords: 4,
        required: true,
      },
      {
        name: 'secondaryAction',
        kind: 'label',
        maxWords: 4,
        required: false,
      },
      {
        name: 'productShot',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
    ],
  },
  {
    id: 'back-cover',
    label: 'Back cover',
    purpose: 'The last frame: leaves only the brand and where to find it.',
    whenToUse:
      'After the ask. No headline and no body; the name is the entire message, with the URL and any legal line set small.',
    compositions: ['full-bleed'],
    slots: [
      {
        name: 'wordmark',
        kind: 'logo',
        maxWords: 3,
        required: true,
      },
      {
        name: 'url',
        kind: 'label',
        maxWords: 2,
        required: true,
      },
      {
        name: 'legalLine',
        kind: 'label',
        maxWords: 6,
        required: false,
      },
      {
        name: 'textureImage',
        kind: 'image',
        maxWords: 0,
        required: false,
      },
    ],
  },
]

export const ROLE_BY_ID: Record<string, SlideRole> = Object.fromEntries(SLIDE_ROLES.map((r) => [r.id, r]))

export function roleById(id: string | undefined): SlideRole | undefined {
  return id ? ROLE_BY_ID[id.trim().toLowerCase()] : undefined
}

/** The roles that suit a composition, best first. Empty when the family fits no harvested role. */
export function rolesForComposition(family: LayoutPattern): SlideRole[] {
  return SLIDE_ROLES.filter((r) => r.compositions.includes(family)).sort(
    (a, b) => a.compositions.indexOf(family) - b.compositions.indexOf(family)
  )
}

const wordRule = (s: RoleSlot): string => {
  if (s.kind === 'image' || s.kind === 'logo' || s.kind === 'chart')
    return s.required ? 'required' : 'optional'
  const cap = `at most ${s.maxWords} word${s.maxWords === 1 ? '' : 's'}`
  return s.required ? cap : `${cap}, optional`
}

/**
 * The ROLE block. Slots with their measured caps, the repeat range, and what the slide is for — the three
 * things that stop a writer from either padding a slide with invented furniture or leaving it half empty.
 *
 * The caps are stated as a budget rather than a style note on purpose: "at most 6 words" is checkable by
 * the writer as it writes, where "keep it short" is not.
 */
export function roleBrief(role: SlideRole): string {
  // Two ways a role repeats, and both come out of the harvest. Usually the unit is described by dotted
  // slots (`item.title`, `item.body`); where the unit is a single thing, the repeat slot IS a top-level
  // slot of that name — an image showcase holds one or two images, and each is just an image.
  const dotted = role.slots.filter((s) => s.name.includes('.'))
  const unit =
    dotted.length > 0 ? dotted : role.repeatSlot ? role.slots.filter((s) => s.name === role.repeatSlot) : []
  const own = role.slots.filter((s) => !unit.includes(s))

  /* A COMPOSITION THIS BRAND INVENTED HAS NO MEASURED SLOTS, and must not be handed the slot block: the
     list would be empty and the sentence that follows it ("Fill every required slot and nothing else.
     Add no slot this list does not name") would then forbid every element on the artifact. The 21 carry
     word caps measured off 61 pages of real decks; a brand naming "Pricing table" has measured nothing,
     so the brief is written from what it DID say — what the composition is for and when to reach for it
     — and the writer decides the slots. See `kitRoles.ts` for why we do not invent caps on its behalf. */
  if (role.slots.length === 0) {
    return [
      `ROLE. This document is a ${role.label.toLowerCase()}: ${role.purpose} ${role.whenToUse}`,
      'This composition has no fixed slot list. Decide what it carries from the purpose above and from',
      'the brand rules that follow, keep it to one idea, and give every element on it a reason to be',
      'there. No decorative furniture, no label that only announces the section, nothing to fill space.',
    ].join('\n')
  }

  const lines = [
    `ROLE. This document is a ${role.label.toLowerCase()}: ${role.purpose} ${role.whenToUse}`,
    'It carries exactly these slots, and the word counts are hard limits measured off real artifacts, not suggestions:',
    ...own.map((s) => `- ${s.name} (${s.kind}): ${wordRule(s)}`),
  ]
  if (unit.length && role.repeatSlot) {
    lines.push(
      `Then ${role.repeatRange ?? '3-4'} of ${role.repeatSlot}, each one:`,
      ...unit.map(
        (s) =>
          `- ${s.name.includes('.') ? s.name.split('.').slice(1).join('.') : s.name} (${s.kind}): ${wordRule(
            s
          )}`
      )
    )
  }
  lines.push(
    'Fill every required slot and nothing else. An optional slot is used only when it carries real content, never to fill space. Add no slot this list does not name: no extra label, no extra caption, no decorative furniture.'
  )
  return lines.join('\n')
}

/**
 * One role per composition family, for the nine kit references.
 *
 * The kit page curates nine references, one per geometry family, and each of them now has to BE something:
 * a split of nothing is a layout exercise, a split carrying a statement is a reference.
 *
 * SCARCEST FAMILY FIRST, and that ordering is the whole algorithm. Walking `PATTERN_ORDER` and letting each
 * family take its favourite gives `ledger` the one metric role and leaves `single-number`, whose only
 * candidate that is, with nothing — the roles are not evenly spread across the geometries, so a family with
 * one option has to choose before a family with nine. Ties keep the vocabulary's own order, so the answer is
 * stable and a regenerated reference is comparable to the one it replaced.
 */
export function assignRolesToCompositions(families: readonly LayoutPattern[]): Record<string, SlideRole> {
  const byScarcity = families
    .map((family, index) => ({ family, index, candidates: rolesForComposition(family) }))
    .sort((a, b) => a.candidates.length - b.candidates.length || a.index - b.index)
  const taken = new Set<string>()
  const out: Record<string, SlideRole> = {}
  for (const { family, candidates } of byScarcity) {
    const pick = candidates.find((r) => !taken.has(r.id)) ?? candidates[0]
    if (!pick) continue
    taken.add(pick.id)
    out[family] = pick
  }
  return out
}

/**
 * The other direction, and the one the kit library actually runs on: a composition for every ROLE.
 *
 * Each role starts on the geometry it suits best. That alone would leave two families with no reference at
 * all — nothing in the harvest calls `ledger` or `single-number` its first choice, they are always a second
 * or third reading of a role that prefers a grid — and a kit whose ledger has no exemplar is a kit that
 * cannot steer a ledger. So any uncovered family is filled by the role that ranks it highest, taken from a
 * family that still has other roles holding it. Deterministic, and it answers the same way every run.
 */
export function assignCompositionsToRoles(
  roles: readonly SlideRole[],
  families: readonly LayoutPattern[]
): Record<string, LayoutPattern> {
  const out: Record<string, LayoutPattern> = {}
  for (const role of roles) out[role.id] = role.compositions[0]

  const holders = (family: LayoutPattern): string[] =>
    roles.filter((r) => out[r.id] === family).map((r) => r.id)
  const movable = (family: LayoutPattern): SlideRole[] =>
    roles
      .filter((r) => r.compositions.includes(family) && holders(out[r.id]).length > 1)
      .sort((a, b) => a.compositions.indexOf(family) - b.compositions.indexOf(family))

  // SCARCEST FAMILY FIRST, the same lesson the other assignment learned. `single-number` is named by
  // exactly one role and `ledger` by three; filling ledger first hands it that one role and leaves
  // single-number with nothing, because a role can only be moved out of a family that still has others.
  const uncovered = families
    .filter((f) => holders(f).length === 0)
    .map((family, index) => ({ family, index, options: movable(family).length }))
    .sort((a, b) => a.options - b.options || a.index - b.index)
  for (const { family } of uncovered) {
    if (holders(family).length > 0) continue
    const candidate = movable(family)[0]
    if (candidate) out[candidate.id] = family
  }
  return out
}
